import { useEffect, useState, useMemo } from "react";
import { Loader2, Plus, Trash2, Save, X, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUS = ["Entwurf", "Veröffentlicht", "Noindex", "Archiviert"];
const SITE_URL = "https://ruempel.taxi";

const empty = {
  name: "", slug: "", postal_codes: [], district: "", state: "", distance_km: null,
  h1: "", meta_title: "", meta_description: "", intro: "", body: "",
  towns: [], object_types: [], audiences: [], highlights: [], services: [], faq: [],
  status: "Entwurf", is_priority: false, canonical: "", quality_ok: false,
  quality_notes: "", order: 0,
};

const statusBadge = {
  "Veröffentlicht": "bg-green-100 text-green-800",
  "Noindex": "bg-amber-100 text-amber-800",
  "Entwurf": "bg-light-gray text-mid-gray",
  "Archiviert": "bg-red-100 text-red-700",
};

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const toCsv = (arr) => (arr || []).join(", ");
const fromCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function Cities() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fStatus, setFStatus] = useState("");
  const [fDistrict, setFDistrict] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fStatus) params.status = fStatus;
      if (fDistrict) params.district = fDistrict;
      if (q) params.q = q;
      const [list, st] = await Promise.all([
        api.get("/admin/cities", { params }),
        api.get("/admin/cities/stats"),
      ]);
      setItems(list.data);
      setStats(st.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [fStatus, fDistrict]);

  const districts = useMemo(
    () => [...new Set(items.map((c) => c.district).filter(Boolean))].sort(),
    [items]
  );

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Bitte einen Ortsnamen angeben.");
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      if (payload.distance_km === "" || payload.distance_km === null) payload.distance_km = null;
      else payload.distance_km = Number(payload.distance_km);
      payload.order = Number(payload.order) || 0;
      if (editing.id) await api.patch(`/admin/cities/${editing.id}`, payload);
      else await api.post("/admin/cities", payload);
      toast.success("Ortsseite gespeichert.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (city, status) => {
    try {
      await api.patch(`/admin/cities/${city.id}`, { ...city, status });
      toast.success(`Status: ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Ortsseite löschen?")) return;
    await api.delete(`/admin/cities/${id}`);
    toast.success("Gelöscht.");
    load();
  };

  const inputCls = "mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow";

  return (
    <div data-testid="admin-cities">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-anthracite">Lokale SEO · Orte</h1>
          <p className="mt-1 text-mid-gray">Ortsseiten verwalten, veröffentlichen und auf noindex setzen.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black hover:bg-yellow-dark" data-testid="city-new">
          <Plus className="h-4 w-4" /> Neuer Ort
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="city-stats">
          {[
            ["Gesamt", stats.gesamt], ["Veröffentlicht", stats.veroeffentlicht],
            ["Noindex", stats.noindex], ["Entwurf", stats.entwurf],
            ["Priorität", stats.prioritaet], ["Prüfung offen", stats.pruefung_offen],
          ].map(([label, val]) => (
            <div key={label} className="rounded-md border border-light-gray bg-white p-4">
              <p className="text-2xl font-bold text-anthracite">{val}</p>
              <p className="text-xs text-mid-gray">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mid-gray" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Ort, PLZ, Slug…"
            className="rounded-sm border border-light-gray bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-yellow"
            data-testid="city-search"
          />
        </div>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="city-filter-status">
          <option value="">Alle Status</option>
          {STATUS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={fDistrict} onChange={(e) => setFDistrict(e.target.value)} className="rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="city-filter-district">
          <option value="">Alle Landkreise</option>
          {districts.map((d) => <option key={d}>{d}</option>)}
        </select>
        <button onClick={load} className="rounded-sm bg-anthracite px-4 py-2 text-sm font-semibold text-white hover:bg-brand-black">Suchen</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>
      ) : items.length === 0 ? (
        <p className="mt-10 rounded-md border border-dashed border-mid-gray/30 bg-white p-12 text-center text-mid-gray" data-testid="cities-empty">Keine Orte gefunden.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-md border border-light-gray bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-light-gray bg-light-gray text-left text-xs uppercase tracking-wider text-mid-gray">
                <th className="px-4 py-3">Ort</th>
                <th className="px-4 py-3">PLZ</th>
                <th className="px-4 py-3">Landkreis</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Prio</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-light-gray/60 hover:bg-light-gray/40" data-testid={`city-row-${c.slug}`}>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-anthracite">{c.name}</span>
                    <span className="block text-xs text-mid-gray">/entruempelung/{c.slug}</span>
                  </td>
                  <td className="px-4 py-3 text-mid-gray">{(c.postal_codes || []).join(", ")}</td>
                  <td className="px-4 py-3 text-mid-gray">{c.district}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-sm px-2 py-1 text-xs font-semibold ${statusBadge[c.status] || "bg-light-gray text-mid-gray"}`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3">{c.is_priority ? "★" : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {c.status === "Veröffentlicht" && (
                        <a href={`${SITE_URL}/entruempelung/${c.slug}`} target="_blank" rel="noreferrer" className="text-mid-gray hover:text-yellow-dark" title="Live ansehen"><ExternalLink className="h-4 w-4" /></a>
                      )}
                      {c.status !== "Veröffentlicht" ? (
                        <button onClick={() => quickStatus(c, "Veröffentlicht")} className="text-xs font-bold text-green-700 hover:underline" data-testid={`city-publish-${c.slug}`}>Veröffentlichen</button>
                      ) : (
                        <button onClick={() => quickStatus(c, "Noindex")} className="text-xs font-bold text-amber-700 hover:underline" data-testid={`city-noindex-${c.slug}`}>Noindex</button>
                      )}
                      <button onClick={() => setEditing(c)} className="text-xs font-bold text-anthracite hover:text-yellow-dark" data-testid={`city-edit-${c.slug}`}>Bearbeiten</button>
                      <button onClick={() => del(c.id)} className="text-red-500" data-testid={`city-del-${c.slug}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-black/60 p-4" data-testid="city-editor">
          <div className="my-8 w-full max-w-3xl rounded-md bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-anthracite">{editing.id ? `Ort bearbeiten: ${editing.name}` : "Neuer Ort"}</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-mid-gray" /></button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-semibold text-anthracite">Ortsname *<input value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} className={inputCls} data-testid="city-field-name" /></label>
              <label className="text-sm font-semibold text-anthracite">URL-Slug<input value={editing.slug} onChange={(e) => setEditing((p) => ({ ...p, slug: e.target.value }))} placeholder="automatisch aus Name" className={inputCls} data-testid="city-field-slug" /></label>
              <label className="text-sm font-semibold text-anthracite">PLZ (Komma-getrennt)<input value={toCsv(editing.postal_codes)} onChange={(e) => setEditing((p) => ({ ...p, postal_codes: fromCsv(e.target.value) }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite">Landkreis<input value={editing.district || ""} onChange={(e) => setEditing((p) => ({ ...p, district: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite">Bundesland<input value={editing.state || ""} onChange={(e) => setEditing((p) => ({ ...p, state: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite">Entfernung (km)<input type="number" value={editing.distance_km ?? ""} onChange={(e) => setEditing((p) => ({ ...p, distance_km: e.target.value }))} className={inputCls} /></label>

              <label className="text-sm font-semibold text-anthracite sm:col-span-2">H1-Überschrift<input value={editing.h1 || ""} onChange={(e) => setEditing((p) => ({ ...p, h1: e.target.value }))} className={inputCls} data-testid="city-field-h1" /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Meta-Title<input value={editing.meta_title || ""} onChange={(e) => setEditing((p) => ({ ...p, meta_title: e.target.value }))} className={inputCls} data-testid="city-field-metatitle" /><span className="text-xs font-normal text-mid-gray">{(editing.meta_title || "").length} Zeichen</span></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Meta-Description<textarea value={editing.meta_description || ""} onChange={(e) => setEditing((p) => ({ ...p, meta_description: e.target.value }))} className={inputCls} rows={2} data-testid="city-field-metadesc" /><span className="text-xs font-normal text-mid-gray">{(editing.meta_description || "").length} Zeichen (Ziel 150–160)</span></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Einleitung (Intro)<textarea value={editing.intro || ""} onChange={(e) => setEditing((p) => ({ ...p, intro: e.target.value }))} className={inputCls} rows={3} /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Fließtext (Absätze durch Leerzeile trennen)<textarea value={editing.body || ""} onChange={(e) => setEditing((p) => ({ ...p, body: e.target.value }))} className={inputCls} rows={6} /></label>

              <label className="text-sm font-semibold text-anthracite">Umliegende Orte (je Zeile)<textarea value={toLines(editing.towns)} onChange={(e) => setEditing((p) => ({ ...p, towns: fromLines(e.target.value) }))} className={inputCls} rows={3} /></label>
              <label className="text-sm font-semibold text-anthracite">Highlights (je Zeile)<textarea value={toLines(editing.highlights)} onChange={(e) => setEditing((p) => ({ ...p, highlights: fromLines(e.target.value) }))} className={inputCls} rows={3} /></label>
              <label className="text-sm font-semibold text-anthracite">Zielgruppen (je Zeile)<textarea value={toLines(editing.audiences)} onChange={(e) => setEditing((p) => ({ ...p, audiences: fromLines(e.target.value) }))} className={inputCls} rows={2} /></label>
              <label className="text-sm font-semibold text-anthracite">Objektarten (je Zeile)<textarea value={toLines(editing.object_types)} onChange={(e) => setEditing((p) => ({ ...p, object_types: fromLines(e.target.value) }))} className={inputCls} rows={2} /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Leistungen (Komma-getrennt)<input value={toCsv(editing.services)} onChange={(e) => setEditing((p) => ({ ...p, services: fromCsv(e.target.value) }))} className={inputCls} /></label>
            </div>

            {/* FAQ */}
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-mid-gray">FAQ</h3>
                <button onClick={() => setEditing((p) => ({ ...p, faq: [...(p.faq || []), { q: "", a: "" }] }))} className="text-xs font-bold text-anthracite hover:text-yellow-dark">+ Frage</button>
              </div>
              <div className="mt-2 space-y-2">
                {(editing.faq || []).map((f, i) => (
                  <div key={i} className="rounded-sm border border-light-gray p-3">
                    <div className="flex items-center gap-2">
                      <input value={f.q} onChange={(e) => setEditing((p) => { const faq = [...p.faq]; faq[i] = { ...faq[i], q: e.target.value }; return { ...p, faq }; })} placeholder="Frage" className="flex-1 rounded-sm border border-light-gray bg-light-gray px-2 py-1.5 text-sm outline-none focus:border-yellow" />
                      <button onClick={() => setEditing((p) => ({ ...p, faq: p.faq.filter((_, j) => j !== i) }))} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <textarea value={f.a} onChange={(e) => setEditing((p) => { const faq = [...p.faq]; faq[i] = { ...faq[i], a: e.target.value }; return { ...p, faq }; })} placeholder="Antwort" className="mt-2 w-full rounded-sm border border-light-gray bg-light-gray px-2 py-1.5 text-sm outline-none focus:border-yellow" rows={2} />
                  </div>
                ))}
              </div>
            </div>

            {/* Verwaltung */}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-semibold text-anthracite">Status<select value={editing.status} onChange={(e) => setEditing((p) => ({ ...p, status: e.target.value }))} className={inputCls} data-testid="city-field-status">{STATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
              <label className="text-sm font-semibold text-anthracite">Reihenfolge<input type="number" value={editing.order} onChange={(e) => setEditing((p) => ({ ...p, order: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Canonical-URL (optional)<input value={editing.canonical || ""} onChange={(e) => setEditing((p) => ({ ...p, canonical: e.target.value }))} placeholder="leer = Standard-Canonical dieser Seite" className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Qualitäts-Notiz<input value={editing.quality_notes || ""} onChange={(e) => setEditing((p) => ({ ...p, quality_notes: e.target.value }))} className={inputCls} /></label>
              <label className="flex items-center gap-2 text-sm text-anthracite"><input type="checkbox" checked={!!editing.is_priority} onChange={(e) => setEditing((p) => ({ ...p, is_priority: e.target.checked }))} className="accent-yellow" /> Prioritätsort</label>
              <label className="flex items-center gap-2 text-sm text-anthracite"><input type="checkbox" checked={!!editing.quality_ok} onChange={(e) => setEditing((p) => ({ ...p, quality_ok: e.target.checked }))} className="accent-yellow" /> Qualität geprüft</label>
            </div>

            <button onClick={save} disabled={saving} className="mt-5 inline-flex items-center gap-2 rounded-sm bg-yellow px-5 py-3 font-heading font-bold text-brand-black hover:bg-yellow-dark disabled:opacity-60" data-testid="city-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
